/**
 * Group companies.
 *
 * The approved mock-up deliberately keeps companies OUT of the navigation — the
 * whole premise is "services, not companies". The routes stay implemented
 * because they are part of the frozen route contract (sitemap, redirects, CMS
 * links), so a deep link resolves instead of 404-ing; discovery happens through
 * "O Grupie".
 */
import { el } from "../core/dom.js";
import type { AppContext } from "../core/context.js";
import type { RouteMatch } from "../core/routes.js";
import { applyPageSeo } from "../core/seo.js";
import { breadcrumbs, sectionHead } from "../components/common.js";
import { relatedServiceCard } from "../components/cards.js";
import { notFoundView } from "./status.js";

export async function companiesListView(ctx: AppContext): Promise<HTMLElement> {
	const copy = ctx.copy();
	const companies = await ctx.gateway.companies();
	const title = ctx.t("nav.companies");

	applyPageSeo(ctx, { title: `${title} — ${copy.brand.name}`, description: copy.about.bodyTitle });

	const grid = el("div", { class: "mb-grid mb-grid--3" });
	for (const company of companies) {
		grid.appendChild(
			el(
				"a",
				{
					class: "mb-card mb-card--service",
					href: ctx.href("companyDetail", { slug: company.slug }),
				},
				el("h3", { class: "mb-card__title" }, company.name),
				el("p", { class: "mb-card__text" }, company.description),
			),
		);
	}

	return el(
		"div",
		{ class: "mb-view mb-view--companies" },
		el(
			"section",
			{ class: "mb-section mb-section--white" },
			el(
				"div",
				{ class: "mb-section__inner" },
				breadcrumbs(ctx, [{ label: copy.nav.start, key: "home" }, { label: title }]),
				el("h1", { class: "mb-pagehead__title" }, copy.about.bodyTitle),
				...copy.about.bodyParagraphs.map((paragraph) =>
					el("p", { class: "mb-pagehead__lead" }, paragraph),
				),
				grid.childElementCount > 0
					? grid
					: el(
							"p",
							{ class: "mb-searchresults__empty" },
							ctx.locale() === "en"
								? "The group's structure is described on the About page."
								: "Struktura grupy jest opisana na stronie „O Grupie”.",
						),
				el(
					"a",
					{ class: "mb-cta mb-cta--outline", href: ctx.href("about"), "data-routekey": "about" },
					copy.nav.about,
				),
			),
		),
	);
}

export async function companyDetailView(ctx: AppContext, match: RouteMatch): Promise<HTMLElement> {
	const copy = ctx.copy();
	const company = await ctx.gateway.company(match.params.slug ?? "");
	if (!company) return notFoundView(ctx);

	applyPageSeo(ctx, {
		title: `${company.name} — ${copy.brand.name}`,
		description: company.description,
	});

	const view = el("div", { class: "mb-view mb-view--company" });

	view.appendChild(
		el(
			"section",
			{ class: "mb-section mb-section--glow" },
			el(
				"div",
				{ class: "mb-section__inner" },
				breadcrumbs(ctx, [
					{ label: copy.nav.start, key: "home" },
					{ label: ctx.t("nav.companies"), key: "companies" },
					{ label: company.name },
				]),
				el("h1", { class: "mb-pagehead__title" }, company.name),
				company.legalName ? el("p", { class: "mb-pagehead__meta" }, company.legalName) : null,
				el("p", { class: "mb-pagehead__lead" }, company.description),
				company.website
					? el(
							"a",
							{
								class: "mb-cta mb-cta--outline",
								href: company.website,
								rel: "external noopener",
								target: "_blank",
							},
							company.website.replace(/^https?:\/\//, ""),
						)
					: null,
			),
		),
	);

	if (company.services.length > 0) {
		const grid = el("div", { class: "mb-grid mb-grid--3" });
		for (const service of company.services) grid.appendChild(relatedServiceCard(ctx, service));
		view.appendChild(
			el(
				"section",
				{ class: "mb-section mb-section--white" },
				el(
					"div",
					{ class: "mb-section__inner" },
					sectionHead(ctx, {
						title: copy.nav.services,
						link: { label: copy.serviceDetail.relatedAll, key: "services" },
						className: "mb-sectionhead--row",
					}),
					grid,
				),
			),
		);
	}

	return view;
}
